import React, { useEffect, useLayoutEffect, useState, useContext, useCallback, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ScannerSection, closeCameraAfterScanFlash, type ScannerSectionHandle } from '../../scanner';
import { isFadingQrScanError } from '../../scanner/qrScanMessages';
import { fetchTags, releaseOrder, splitOrderInShipStation } from '../../../services/shipStationAPI';
import { performOCR } from '../../../services/visionAPI';
import { deletePackingLog, submitPackingLog } from '../../../services/packingLogAPI';
import { printQrLabel } from '../../../services/printQrLabelAPI';
import { recordFulfillmentEvent } from '../../../services/fulfillmentEventsAPI';
import { fetchOrderShelfLocations } from '../../../services/skuShelfIndexAPI';
import { fetchSkusWithNotes } from '../../../services/productWarehouseNotesAPI';
import { getLabelStatus, getLabelByStorageIdPrefix } from '../../../services/shelfIndexAPI';
import { OrderContext } from '../../../contexts/OrderContext';
import { 
  handleSubmit as handleSubmitCore,
  handleReset as handleResetCore,
  pickLineUnitCount,
} from './orderDataLogic';
import { handleCapture as handleCaptureCore, handleQrCapture as handleQrCaptureCore, buildBundleScanSubsetBomMap, targetedScanFromPickItem, type ScanMatchResult, type BundleScanSubsetBomMap, type TargetedScan } from './scanningLogic';
import { fetchBundleScanSubset } from '../../../services/bundleScanSubsetAPI';
import { applyBundleFloorBinDeltas } from '../../../services/bundleFloorAPI';
import {
  pendingBundleBinDecrements,
  recordBundleBinScans,
} from './bundleWorkOrderLogic';
import {
  SCAN_SESSION_CLEARED_EVENT,
  markOrderItemsScanned,
  type BundleBinScanMap,
} from '../../../utils/scanSessionCache';
import { applyShelfLocationToOrderItem, getShelfLookupSkus } from './shelfUtils';
import {
  dropOrderDetailPrefetch,
  loadOrderDetailContent,
  peekOrderDetailPrefetch,
  takeResolvedOrderDetailPrefetch,
  type OrderDetailContent,
} from './orderDetailPrefetch';
import useVisibleItems from './useVisibleItems';
import { usePickViewTouchLock } from './usePickViewTouchLock';
import { useAutoOpenOrderDetailsCamera } from './useAutoOpenOrderDetailsCamera';
import { useHybridShelfIndexSync } from '../../../hooks/useHybridShelfIndexSync';
import { useOrderDetailScanMode } from '../../../hooks/useOrderDetailScanMode';
import { useOrderPickScanRealtime } from '../../../hooks/useOrderPickScanRealtime';
import { FULFILLMENT_EVENT_TYPES, ORDER_RELEASE_REASONS, type LabelScanFailedMetadata, type OrderReleaseReason } from '@/shared/types/fulfillmentEvents.types';
import { useAuth } from '../../../contexts/AuthContext';
import { useUserSettings } from '../../../contexts/UserSettingsContext';
import { getTagNameFromEmail, tagDictionaryMaker } from '../../../contexts/TagStorage';
import { getApiErrorMessage } from '../../../services/errorUtils';
import {
  applyOrderPickScanOverlay,
  binsFromOrderPickScans,
  clearOrderPickScans,
  hydrateSharedOrderScans,
  saveOrderPickScans,
  type OrderPickScanOverlay,
} from '../../../services/orderPickScanAPI';
import type { OrderPickScanLine } from '@/shared/utils/orderPickScan';
import ProductInfoModal from '../../shared/ProductInfoModal';
import { FaFingerprint, FaHashtag, FaPuzzlePiece } from 'react-icons/fa';
import { QrPlaceholderIcon } from '../../scanner/WebcamPlaceholder';
import type { OrderItem } from '../../../types/apiTypes';
import OrderDetailNavbar from './OrderDetailNavbar';
import OrderDetailCameraBar, { CameraIcon, PICK_SCAN_BAR_VARIANTS } from './OrderDetailCameraBar';
import OrderActionsMenu from './OrderActionsMenu';
import type { PickScanBarVariant } from './OrderDetailCameraBar';
import OrderItemsList from './OrderItemsList';
import PackingPhotoSheet, { PACKING_PHOTO_PHASES, type PackingPhotoPhase } from './PackingPhotoSheet';
import BoxDimensionConfirmModal, { boxDimensionConfirmMessage } from './BoxDimensionConfirmModal';
import SuggestedBoxSheet from './SuggestedBoxSheet';
import SelectBoxPrompt from './SelectBoxPrompt';
import {
  POST_SUBMIT_STEP_KINDS,
  loadPostSubmitStep,
  writePostSubmitStep,
  type PostSubmitStep,
} from './postSubmitStep';
import type { BoxDimensions } from '../parseBoxDimensions';
import {
  BOX_SCAN_NO_TEXT_MESSAGE,
  BOX_SCAN_STATUSES,
  boxScanNoDimsMessage,
  readBoxDimensionsFromImage,
} from '../boxDimensionScan';
import { lookupOrderPackageDimensions, pushOrderPackageDimensionsToShipStation, saveOrderPackageDimensions } from '../../../services/orderPackageDimensionsAPI';
import { packageCompositionSkus } from '@/shared/utils/packageSkuComposition';
import {
  copyPackingFrame,
  isPackingJpegCancelled,
  startPackingJpegEncode,
  type PackingJpegEncode,
} from './encodePackingJpeg';
import { deletePackingLogImages, fetchNextPackingPhotoSequence, savePackingPhoto } from './savePackingPhoto';
import type { WebcamViewportHandle } from '../../scanner/WebcamViewport';
import { orderBenefitsFromPuzzleView } from './ComponentPickList';
import SplitShipmentView from './SplitShipmentView';
import {
  SPLIT_HOLD_MODES,
  buildSplitLines,
  canConfirmSplit,
  initialSplitBoxes,
  splitBoxesToRequest,
  type SplitBoxes,
  type SplitHoldMode,
} from './splitShipmentBoxes';
import '../../labels/PrintBoxLabel.css';
import './OrderDetail.css';

const MAX_FAILED_SCAN_OCR_TEXT_LENGTH = 500;
/** Matches SHIPSTATION_TAG_IDS.IN_PROGRESS on the server. */
const IN_PROGRESS_TAG_ID = 138117;
/** Matches SHIPSTATION_TAG_IDS.CHECK_SLACK on the server. */
const CHECK_SLACK_TAG_ID = 132307;
/** Legacy in-page scanner block (camera toggle + preview). Action icons live on the bottom bar. */
const SHOW_ORDER_DETAIL_SCANNER = false;

const PACKING_SUBMIT_FLASHES = {
  SUCCESS: 'success',
  NO_PHOTO: 'no-photo',
} as const;

type PackingSubmitFlash = (typeof PACKING_SUBMIT_FLASHES)[keyof typeof PACKING_SUBMIT_FLASHES];

interface PostSubmitRestore {
  readonly orderId: string;
  readonly submitted: boolean;
  readonly step: PostSubmitStep | null;
}

const ORDER_LABEL_SENT_MESSAGE = 'Order label sent to printer';

function isSuccessMessage(msg: string): boolean {
  return (
    msg === 'Order submitted successfully!' ||
    msg === 'Order reset successfully' ||
    msg === ORDER_LABEL_SENT_MESSAGE ||
    (msg.startsWith('SKU ') && msg.includes('matched'))
  );
}

function isStatusMessage(msg: string): boolean {
  return msg.includes('Processing image') || msg.includes('Looking up label');
}

function orderDetailMessageClass(msg: string): string {
  if (isSuccessMessage(msg)) return 'order-detail-message order-detail-message-success';
  if (isStatusMessage(msg)) return 'order-detail-message order-detail-message-default';
  if (isFadingQrScanError(msg)) return 'order-detail-message order-detail-message-error webcam-qr-miss-fade';
  return 'order-detail-message order-detail-message-error';
}

const RELEASE_REASON_LABELS: Readonly<Record<OrderReleaseReason, string>> = {
  [ORDER_RELEASE_REASONS.TESTING]: 'Testing / picked by mistake',
  [ORDER_RELEASE_REASONS.OUT_OF_STOCK]: 'Out of stock (Hold for CS)',
  [ORDER_RELEASE_REASONS.OTHER]: 'Other',
};

function buildFailedScanMetadata(
  failure: NonNullable<ScanMatchResult['failure']>,
): LabelScanFailedMetadata {
  return {
    reason: failure.reason,
    ...(failure.ocrText
      ? { ocr_text: failure.ocrText.slice(0, MAX_FAILED_SCAN_OCR_TEXT_LENGTH) }
      : {}),
  };
}

function OrderDetail() {
  const { user, isAdmin } = useAuth();
  const { settings } = useUserSettings();
  const cameraOpensOrderDetails = settings.cameraOpensOrderDetails;
  const ctx = useContext(OrderContext);
  if (!ctx) throw new Error('OrderDetail must be used within an OrderProvider');
  const {
    orderItems,
    setOrderItems,
    setSelectedOrderId,
    shipToName,
    setShipToName,
    orderNumberCin7,
    setOrderNumberCin7,
    setTitle,
  } = ctx;
  const navigate = useNavigate();
  const { orderId: shipStationOrderId } = useParams();
  const sessionKey = shipStationOrderId ?? '';
  const [message, setMessage] = useState<string>('');
  const [isReadyForSubmit, setIsReadyForSubmit] = useState(false);
  const [loading, setLoading] = useState(true);
  const [showSuccess, setShowSuccess] = useState(false);
  const [orderFlash, setOrderFlash] = useState<PackingSubmitFlash>(PACKING_SUBMIT_FLASHES.SUCCESS);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [packingPhotoPhase, setPackingPhotoPhase] = useState<PackingPhotoPhase | null>(null);
  const [packingPhotoFrame, setPackingPhotoFrame] = useState<HTMLCanvasElement | null>(null);
  const [packingPhotoSubmitting, setPackingPhotoSubmitting] = useState(false);
  const [packingPhotoBusyLabel, setPackingPhotoBusyLabel] = useState('Saving packing photo');
  const [packingPhotoError, setPackingPhotoError] = useState('');
  const [packingPhotoRetake, setPackingPhotoRetake] = useState(false);
  const [boxScanOpen, setBoxScanOpen] = useState(false);
  const [boxScanBusy, setBoxScanBusy] = useState(false);
  const [boxScanError, setBoxScanError] = useState('');
  const [pendingBoxDims, setPendingBoxDims] = useState<BoxDimensions | null>(null);
  const [boxDimsSaving, setBoxDimsSaving] = useState(false);
  const [boxSuggestion, setBoxSuggestion] = useState<BoxDimensions | null>(null);
  const [boxSuggestionError, setBoxSuggestionError] = useState('');
  const [boxSelectPrompt, setBoxSelectPrompt] = useState(false);
  const [postSubmitRestore, setPostSubmitRestore] = useState<PostSubmitRestore | null>(null);
  const [selectedProductInfo, setSelectedProductInfo] = useState<OrderItem | null>(null);
  const [webcamOn, setWebcamOn] = useState(false);
  const [componentsOnlyMode, setComponentsOnlyMode] = useState(false);
  const { qrActive, toggleScanMode } = useOrderDetailScanMode();
  const [prefixToSkuMap, setPrefixToSkuMap] = useState<Record<string, string>>({});
  const [subsetBomMap, setSubsetBomMap] = useState<BundleScanSubsetBomMap>(() => new Map());
  // Bundle-parent pick lines whose pick bin should be decremented, held until submit.
  const [pendingBinScans, setPendingBinScans] = useState<BundleBinScanMap>({});
  const [skusWithNotes, setSkusWithNotes] = useState<Record<string, true>>({});
  const [orderTagIds, setOrderTagIds] = useState<number[]>([]);
  const [userTagId, setUserTagId] = useState<number | null>(null);
  const [showReleasePicker, setShowReleasePicker] = useState(false);
  const [selectedReleaseReason, setSelectedReleaseReason] = useState<OrderReleaseReason | null>(null);
  const [releasing, setReleasing] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [reprintingOrderLabel, setReprintingOrderLabel] = useState(false);
  const [checkSlackAcknowledged, setCheckSlackAcknowledged] = useState(false);
  const [isInternational, setIsInternational] = useState(false);
  const [actionMenuOpen, setActionMenuOpen] = useState(false);
  const [splitBoxes, setSplitBoxes] = useState<SplitBoxes | null>(null);
  const splitShipmentOpen = splitBoxes != null;
  const [splitHoldMode, setSplitHoldMode] = useState<SplitHoldMode>(SPLIT_HOLD_MODES.HALF);
  const [splitSubmitting, setSplitSubmitting] = useState(false);
  const scannerCaptureRef = useRef<ScannerSectionHandle>(null);
  const packingPhotoViewportRef = useRef<WebcamViewportHandle>(null);
  const packingPhotoOpenGenerationRef = useRef(0);
  const packingPhotoSequenceRef = useRef<number | null>(null);
  const packingPhotoSequencePromiseRef = useRef<Promise<number> | null>(null);
  const packingPhotoBusyRef = useRef(false);
  const packingPhotoBlobRef = useRef<Blob | null>(null);
  const packingJpegJobRef = useRef<PackingJpegEncode | null>(null);
  const releasePackingPhotoStill = useCallback((): void => {
    packingJpegJobRef.current?.cancel();
    packingJpegJobRef.current = null;
    packingPhotoBlobRef.current = null;
    setPackingPhotoFrame(null);
  }, []);
  const boxScanBusyRef = useRef(false);
  const boxDimRetakeRef = useRef(false);
  const boxLookupRef = useRef<Promise<BoxDimensions | null> | null>(null);
  /** Settled lookup. `undefined` means the request is still running. */
  const boxLookupResultRef = useRef<BoxDimensions | null | undefined>(undefined);
  const matchedBoxShipStationWriteRef = useRef<Promise<void>>(Promise.resolve());
  const boxStepGenerationRef = useRef(0);
  /** Order whose saved post-submit step has been restored; writes wait for it so a fresh mount cannot erase the step. */
  const postSubmitReadyKeyRef = useRef('');
  const shelfScanTargetRef = useRef<TargetedScan | null>(null);
  const successFadeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const wasProductModalOpenRef = useRef(false);
  const { getVisibleItems, observeItem } = useVisibleItems();

  const isMountedRef = useRef(true);
  useEffect(() => () => { isMountedRef.current = false; }, []);
  const orderItemsRef = useRef(orderItems);
  orderItemsRef.current = orderItems;
  const scanOverlayRef = useRef<OrderPickScanOverlay>({
    cleared: false,
    added: new Map(),
    removed: new Set(),
  });

  usePickViewTouchLock();

  useEffect(() => {
    if (showReleasePicker) {
      setSelectedReleaseReason(null);
    }
  }, [showReleasePicker]);

  useEffect(() => {
    let cancelled = false;

    const resolveUserTag = async () => {
      if (!user?.email) {
        setUserTagId(null);
        return;
      }
      try {
        const tagsResponse = await fetchTags();
        if (cancelled) return;
        const tagDictionary = tagDictionaryMaker(tagsResponse);
        const tagName = getTagNameFromEmail(user.email, tagDictionary);
        const resolvedTag = tagName ? tagDictionary[tagName] : undefined;
        setUserTagId(typeof resolvedTag === 'number' ? resolvedTag : null);
      } catch (error: unknown) {
        console.error('Error resolving user tag:', error);
        if (!cancelled) setUserTagId(null);
      }
    };

    void resolveUserTag();
    return () => { cancelled = true; };
  }, [user?.email]);

  const canReleaseOrder =
    !isSubmitted &&
    userTagId != null &&
    (orderTagIds.includes(userTagId) ||
      (isAdmin && orderTagIds.includes(IN_PROGRESS_TAG_ID)));

  const pickListComponentsOnly = componentsOnlyMode;

  useEffect(() => {
    if (isSuccessMessage(message) || isFadingQrScanError(message)) {
      successFadeTimerRef.current = setTimeout(() => setMessage(''), 4000);
      return () => {
        if (successFadeTimerRef.current) {
          clearTimeout(successFadeTimerRef.current);
        }
      };
    }
  }, [message]);

  useEffect(() => {
    let cancelled = false;
    const loadSubset = async (): Promise<void> => {
      try {
        const entries = await fetchBundleScanSubset();
        if (cancelled) return;
        setSubsetBomMap(buildBundleScanSubsetBomMap(entries));
      } catch (err: unknown) {
        console.error('Failed to load bundle scan subset:', err);
        if (!cancelled) {
          setSubsetBomMap(new Map());
        }
      }
    };
    void loadSubset();
    return () => {
      cancelled = true;
    };
  }, []);

  const appliedPrefetchOrderRef = useRef('');
  const needsShelfRetryRef = useRef(false);

  const applyOrderDetailContent = useCallback((content: OrderDetailContent): void => {
    setShipToName(content.shipToName);
    setIsInternational(content.isInternational);
    setOrderNumberCin7(content.orderNumber);
    setTitle(content.title);
    setOrderTagIds(content.tagIds);
    setIsSubmitted(content.submitted);
    setPrefixToSkuMap(content.prefixToSkuMap);
    setSkusWithNotes(content.skusWithNotes);
    if (content.scanMessage) setMessage(content.scanMessage);
    const merged = applyOrderPickScanOverlay(
      { items: content.items, bins: content.bins },
      scanOverlayRef.current,
    );
    setOrderItems(merged.items);
    setPendingBinScans(merged.bins);
    setPostSubmitRestore({
      orderId: sessionKey,
      submitted: content.submitted,
      step: content.submitted ? loadPostSubmitStep(sessionKey) : null,
    });
    needsShelfRetryRef.current = !content.shelfLocationsLoaded;
  }, [
    sessionKey,
    setOrderItems,
    setOrderNumberCin7,
    setShipToName,
    setTitle,
  ]);

  // Clear stale pick state before paint so the save effect cannot write the previous
  // order's scanned IDs under the new orderId (e.g. Pick Next without a pre-nav clear).
  // A resolved pick-next prefetch replaces that clear in the same layout pass.
  useLayoutEffect(() => {
    setOrderItems([]);
    setShipToName('');
    setOrderNumberCin7('');
    setTitle('');
    setIsInternational(false);
    setSelectedOrderId(sessionKey);
    setCheckSlackAcknowledged(false);
    setMessage('');
    setShowSuccess(false);
    setOrderFlash(PACKING_SUBMIT_FLASHES.SUCCESS);
    setIsReadyForSubmit(false);
    setIsSubmitted(false);
    setPackingPhotoPhase(null);
    releasePackingPhotoStill();
    setPackingPhotoSubmitting(false);
    setPackingPhotoError('');
    setBoxSelectPrompt(false);
    setSplitBoxes(null);
    setPostSubmitRestore(null);
    postSubmitReadyKeyRef.current = '';
    packingPhotoOpenGenerationRef.current += 1;
    packingPhotoSequenceRef.current = null;
    packingPhotoSequencePromiseRef.current = null;
    matchedBoxShipStationWriteRef.current = Promise.resolve();
    setWebcamOn(false);
    setPendingBinScans({});
    scanOverlayRef.current = { cleared: false, added: new Map(), removed: new Set() };
    if (successFadeTimerRef.current) {
      clearTimeout(successFadeTimerRef.current);
      successFadeTimerRef.current = null;
    }

    const prefetched = takeResolvedOrderDetailPrefetch(sessionKey);
    if (prefetched) {
      applyOrderDetailContent(prefetched);
      setLoading(false);
      appliedPrefetchOrderRef.current = sessionKey;
      return;
    }
    appliedPrefetchOrderRef.current = '';
    setLoading(true);
  }, [
    sessionKey,
    applyOrderDetailContent,
    setOrderItems,
    setShipToName,
    setOrderNumberCin7,
    setTitle,
    setSelectedOrderId,
    releasePackingPhotoStill,
  ]);

  const showCheckSlackModal =
    !loading &&
    !checkSlackAcknowledged &&
    orderTagIds.includes(CHECK_SLACK_TAG_ID);

  useEffect(() => {
    if (appliedPrefetchOrderRef.current === sessionKey) return undefined;
    let cancelled = false;

    setSelectedOrderId(sessionKey);
    setSkusWithNotes({});
    setLoading(true);

    const loadShipStationOrder = async (): Promise<void> => {
      try {
        const pending = peekOrderDetailPrefetch(sessionKey);
        const content = await (pending ?? loadOrderDetailContent(sessionKey));
        if (cancelled) return;
        applyOrderDetailContent(content);
        dropOrderDetailPrefetch(sessionKey);
      } catch (error: unknown) {
        if (cancelled) return;
        console.error('Error fetching order details:', error);
        setMessage('Error loading order details. Please try again.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    void loadShipStationOrder();

    return () => { cancelled = true; };
  }, [
    sessionKey,
    shipStationOrderId,
    applyOrderDetailContent,
    setSelectedOrderId,
    setOrderItems,
    setShipToName,
    setOrderNumberCin7,
    setTitle,
  ]);

  useAutoOpenOrderDetailsCamera({
    sessionKey,
    loading,
    enabled: cameraOpensOrderDetails && !showCheckSlackModal,
    itemCount: orderItems.length,
    allPickLinesScanned: orderItems.length > 0 && orderItems.every((item: OrderItem) => item.scanned),
    isSubmitted,
    setWebcamOn,
  });

  const refreshShelfLocations = useCallback(async () => {
    if (!isMountedRef.current) return;
    const uniqueSkus = getShelfLookupSkus(orderItems);
    if (uniqueSkus.length === 0) {
      return;
    }

    let shelfLocationsMap: Awaited<ReturnType<typeof fetchOrderShelfLocations>>;
    let notesMap: Awaited<ReturnType<typeof fetchSkusWithNotes>>;
    try {
      [shelfLocationsMap, notesMap] = await Promise.all([
        fetchOrderShelfLocations(uniqueSkus),
        fetchSkusWithNotes(uniqueSkus),
      ]);
    } catch (error: unknown) {
      console.error('Error refreshing shelf locations:', error);
      return;
    }
    setSkusWithNotes(notesMap);
    setOrderItems((prevItems: OrderItem[]) => {
      let hasChanges = false;
      const nextItems = prevItems.map((item: OrderItem) => {
        const skuKey = item.sku?.toUpperCase();
        const loc = shelfLocationsMap[skuKey] || { shelfIndex: null, type: null };
        const nextItem = applyShelfLocationToOrderItem(item, loc);
        const prevShelfDisplayValue =
          typeof item.shelfDisplay === 'string'
            ? item.shelfDisplay
            : item.shelfDisplay?.stockLocation || null;
        const nextShelfDisplayValue =
          typeof nextItem.shelfDisplay === 'string'
            ? nextItem.shelfDisplay
            : nextItem.shelfDisplay?.stockLocation || null;
        if (nextItem.shelfIndex !== item.shelfIndex || prevShelfDisplayValue !== nextShelfDisplayValue) {
          hasChanges = true;
        }
        return nextItem;
      });
      return hasChanges ? nextItems : prevItems;
    });
  }, [orderItems, setOrderItems]);

  useEffect(() => {
    if (!needsShelfRetryRef.current || loading || orderItems.length === 0) {
      return;
    }
    needsShelfRetryRef.current = false;
    void refreshShelfLocations();
  }, [loading, orderItems.length, refreshShelfLocations]);

  useHybridShelfIndexSync({
    enabled: Boolean(sessionKey) && !loading && orderItems.length > 0,
    onSync: refreshShelfLocations,
    pollMs: 20000,
    tables: ['shelf_indexes', 'product_warehouse_notes'],
  });

  useEffect(() => {
    const wasOpen = wasProductModalOpenRef.current;
    wasProductModalOpenRef.current = selectedProductInfo !== null;
    if (!wasOpen || selectedProductInfo !== null) {
      return;
    }
    const uniqueSkus = getShelfLookupSkus(orderItems);
    if (uniqueSkus.length === 0) {
      return;
    }
    void fetchSkusWithNotes(uniqueSkus).then(setSkusWithNotes);
  }, [selectedProductInfo, orderItems]);

  useEffect(() => {
    if (!orderItems.some((item: OrderItem) => item.isComponent)) {
      setComponentsOnlyMode(false);
    }
    const allPickLinesScanned =
      orderItems.length > 0 && orderItems.every((item: OrderItem) => item.scanned);
    setIsReadyForSubmit(allPickLinesScanned);
    if (!allPickLinesScanned) return undefined;
    const timer = closeCameraAfterScanFlash(setWebcamOn);
    return () => clearTimeout(timer);
  }, [orderItems]);

  const recordFloorDecrement = useCallback((scanResult: ScanMatchResult): void => {
    const decrement = scanResult.bundleParentFloorDecrement;
    if (!decrement) return;
    setPendingBinScans((prev) => recordBundleBinScans(prev, decrement));
  }, []);

  const markSharedLines = useCallback((lines: readonly OrderPickScanLine[]): void => {
    if (lines.length === 0) return;
    const overlay = scanOverlayRef.current;
    const ids = new Set<string>();
    for (const line of lines) {
      overlay.removed.delete(line.lineId);
      overlay.added.set(line.lineId, line);
      ids.add(line.lineId);
    }
    setOrderItems((prev) => prev.map((item) => (
      ids.has(item.id) ? { ...item, scanned: true } : item
    )));
    setPendingBinScans((prev) => ({ ...prev, ...binsFromOrderPickScans(lines) }));
  }, [setOrderItems]);

  const clearSharedLines = useCallback((): void => {
    scanOverlayRef.current = { cleared: true, added: new Map(), removed: new Set() };
    setOrderItems((prev) => prev.map((item) => (
      item.scanned ? { ...item, scanned: false } : item
    )));
    setPendingBinScans({});
  }, [setOrderItems]);

  const removeSharedLine = useCallback((lineId: string): void => {
    const overlay = scanOverlayRef.current;
    overlay.added.delete(lineId);
    overlay.removed.add(lineId);
    setOrderItems((prev) => prev.map((item) => (
      item.id === lineId ? { ...item, scanned: false } : item
    )));
    setPendingBinScans((prev) => {
      if (!(lineId in prev)) return prev;
      const next = { ...prev };
      delete next[lineId];
      return next;
    });
  }, [setOrderItems]);

  useOrderPickScanRealtime({
    orderId: sessionKey,
    enabled: sessionKey !== '',
    onLines: markSharedLines,
    onCleared: clearSharedLines,
    onLineRemoved: removeSharedLine,
  });

  useEffect(() => {
    const onCleared = (): void => {
      if (sessionKey === '') return;
      void hydrateSharedOrderScans(sessionKey, orderItemsRef.current)
        .then((shared) => {
          const merged = applyOrderPickScanOverlay(shared, scanOverlayRef.current);
          setOrderItems(merged.items);
          setPendingBinScans(merged.bins);
        })
        .catch((error: unknown) => {
          console.error('Error restoring shared scans after session clear:', error);
        });
    };
    window.addEventListener(SCAN_SESSION_CLEARED_EVENT, onCleared);
    return () => window.removeEventListener(SCAN_SESSION_CLEARED_EVENT, onCleared);
  }, [sessionKey, setOrderItems]);

  const persistNewlyScanned = useCallback((
    before: readonly OrderItem[],
    after: readonly OrderItem[],
    scanResult: ScanMatchResult,
  ): void => {
    if (sessionKey === '') return;
    const beforeIds = new Set(before.filter((item) => item.scanned).map((item) => item.id));
    const newly = after.filter((item) => item.scanned && !beforeIds.has(item.id));
    if (newly.length === 0) return;
    const decrement = scanResult.bundleParentFloorDecrement;
    const decrementIds = new Set(decrement?.itemIds ?? []);
    const lines: OrderPickScanLine[] = newly.map((item) => ({
      lineId: item.id,
      sku: decrementIds.has(item.id) ? (decrement?.parentSku ?? null) : null,
      storageIdPrefix: decrementIds.has(item.id) ? (decrement?.storageIdPrefix ?? null) : null,
    }));
    void saveOrderPickScans(sessionKey, lines).catch((error: unknown) => {
      const failed = new Set(lines.map((line) => line.lineId));
      setOrderItems((prev) => prev.map((item) => (
        failed.has(item.id) ? { ...item, scanned: false } : item
      )));
      setPendingBinScans((prev) => {
        const next = { ...prev };
        for (const lineId of failed) {
          delete next[lineId];
        }
        return next;
      });
      setMessage(getApiErrorMessage(error, 'Failed to save scan'));
    });
  }, [sessionKey, setOrderItems]);

  const handleMarkScanned = (): void => {
    setActionMenuOpen(false);
    const before = orderItems;
    const nextItems = markOrderItemsScanned(before);
    setOrderItems(nextItems);
    persistNewlyScanned(before, nextItems, { matched: true, matchedSku: null });
  };

  const consumeShelfScanTarget = (): TargetedScan | undefined => {
    const target = shelfScanTargetRef.current ?? undefined;
    shelfScanTargetRef.current = null;
    return target;
  };

  const handleSelectShelf = (item: OrderItem): void => {
    if (!webcamOn) {
      return;
    }
    const target = targetedScanFromPickItem(item);
    if (!target) {
      return;
    }
    shelfScanTargetRef.current = target;
    scannerCaptureRef.current?.capture();
  };

  const finishScan = (
    before: readonly OrderItem[],
    scanResult: ScanMatchResult,
    nextItems: OrderItem[] | null,
    recordFailure: boolean,
  ): boolean => {
    recordFloorDecrement(scanResult);
    if (nextItems) {
      persistNewlyScanned(before, nextItems, scanResult);
    }
    if (scanResult.matched && shipStationOrderId) {
      void recordFulfillmentEvent(FULFILLMENT_EVENT_TYPES.LABEL_SCAN, shipStationOrderId, scanResult.matchedSku ? { sku: scanResult.matchedSku } : undefined);
    } else if (recordFailure && scanResult.failure && shipStationOrderId) {
      void recordFulfillmentEvent(
        FULFILLMENT_EVENT_TYPES.LABEL_SCAN_FAILED,
        shipStationOrderId,
        buildFailedScanMetadata(scanResult.failure),
      );
    }
    return scanResult.matched;
  };

  const handleCapture = async (imageSrc: string | null): Promise<boolean> => {
    const target = consumeShelfScanTarget();
    if (!imageSrc || !sessionKey) return false;
    const before = orderItems;
    let nextItems: OrderItem[] | null = null;
    const visibleItems = getVisibleItems();
    const scanResult = await handleCaptureCore(
      imageSrc,
      performOCR,
      before,
      (items) => {
        nextItems = items;
        setOrderItems(items);
      },
      setMessage,
      visibleItems,
      pickListComponentsOnly,
      subsetBomMap,
      undefined,
      target,
    );
    return finishScan(before, scanResult, nextItems, true);
  };

  const handleQrCapture = async (qrData: string | null): Promise<boolean> => {
    const target = consumeShelfScanTarget();
    if (!qrData || !sessionKey) return false;
    const before = orderItems;
    let nextItems: OrderItem[] | null = null;
    const visibleItems = getVisibleItems();
    const scanResult = await handleQrCaptureCore(
      qrData,
      prefixToSkuMap,
      getLabelStatus,
      getLabelByStorageIdPrefix,
      before,
      (items) => {
        nextItems = items;
        setOrderItems(items);
      },
      setMessage,
      visibleItems,
      pickListComponentsOnly,
      subsetBomMap,
      undefined,
      target,
    );
    return finishScan(before, scanResult, nextItems, false);
  };

  /**
   * Commits the pick-bin decrements accumulated during this pick. Runs only after the
   * packing log is written, and never blocks a submitted order: a bin that cannot be
   * decremented is logged and the pending map is cleared either way.
   */
  const commitPendingBinDecrements = useCallback(async (): Promise<void> => {
    const deltas = pendingBundleBinDecrements(pendingBinScans, orderItems);
    setPendingBinScans({});
    if (deltas.length === 0) return;

    try {
      const batch = await applyBundleFloorBinDeltas(deltas, 'order-submit');
      const failed = batch.results.filter((entry) => !entry.applied);
      if (failed.length > 0) {
        console.error('Bundle floor bin decrements failed:', failed);
      }
    } catch (error: unknown) {
      console.error('Failed to commit bundle floor bin decrements:', error);
    }
  }, [orderItems, pendingBinScans]);

const handleSubmit = async (options?: { readonly flash?: boolean }): Promise<string | undefined> => {
  const result = await handleSubmitCore(orderItems, shipToName, orderNumberCin7, submitPackingLog, setMessage);
  if (result === 'Order submitted successfully!') {
      void commitPendingBinDecrements();
      if (shipStationOrderId) {
        // line_count mirrors the non-component rows written to packing_log;
        // unit_count counts pick rows, with adjusted-qty rows contributing their quantity.
        const lineCount = orderItems.filter((item: OrderItem) => !item.isComponent).length;
        const unitCount = orderItems
          .filter((item: OrderItem) => !item.isBundleParent)
          .reduce((sum: number, item: OrderItem) => sum + pickLineUnitCount(item), 0);
        void recordFulfillmentEvent(FULFILLMENT_EVENT_TYPES.ORDER_SUBMIT, shipStationOrderId, { line_count: lineCount, unit_count: unitCount });
      }
      if (options?.flash !== false) {
        setShowSuccess(true);
        setTimeout(() => setShowSuccess(false), 1000);
      }
      setIsSubmitted(true);
  }
  return result;
};

  const openBoxScan = (retake = false): void => {
    boxDimRetakeRef.current = retake;
    setWebcamOn(false);
    setPackingPhotoPhase(null);
    releasePackingPhotoStill();
    setPackingPhotoError('');
    setBoxSuggestion(null);
    setBoxSuggestionError('');
    setBoxSelectPrompt(false);
    setBoxScanError('');
    setPendingBoxDims(null);
    setBoxScanOpen(true);
  };

  const queueMatchedBoxShipStationWrite = (dimensions: BoxDimensions): void => {
    if (!shipStationOrderId) return;
    const orderId = shipStationOrderId;
    matchedBoxShipStationWriteRef.current = matchedBoxShipStationWriteRef.current.then(async () => {
      try {
        await pushOrderPackageDimensionsToShipStation(orderId, dimensions);
      } catch (error: unknown) {
        console.error('Error writing matched box size to ShipStation:', error);
      }
    });
  };

  const finishPhotoBoxStep = (suggestion: BoxDimensions | null): void => {
    if (suggestion) {
      setBoxSelectPrompt(false);
      setBoxSuggestionError('');
      setBoxSuggestion(suggestion);
      queueMatchedBoxShipStationWrite(suggestion);
      return;
    }
    setBoxSuggestion(null);
    setBoxSelectPrompt(true);
  };

  /** Runs after the packing log posts: box-size step when that setting is on, otherwise straight to the nav bar. */
  const finishPostSubmit = async (flash: PackingSubmitFlash): Promise<void> => {
    const generation = boxStepGenerationRef.current;
    const boxStep = settings.scanSuggestBoxSize;
    const suggestion = !boxStep
      ? null
      : boxLookupResultRef.current !== undefined
        ? boxLookupResultRef.current
        : await (boxLookupRef.current ?? Promise.resolve(null));
    if (boxStepGenerationRef.current !== generation) return;
    clearPackingPhotoSequence();
    setPackingPhotoPhase(null);
    releasePackingPhotoStill();
    if (boxStep) {
      finishPhotoBoxStep(suggestion);
    }
    setOrderFlash(flash);
    setShowSuccess(true);
    setTimeout(() => {
      if (boxStepGenerationRef.current !== generation) return;
      setShowSuccess(false);
      setOrderFlash(PACKING_SUBMIT_FLASHES.SUCCESS);
    }, 1000);
  };

  const declineSuggestedBox = (): void => {
    setBoxSuggestion(null);
    setBoxSuggestionError('');
    openBoxScan();
  };

  const acceptSuggestedBox = async (): Promise<void> => {
    if (!boxSuggestion || !shipStationOrderId || boxDimsSaving) return;
    const orderNumber = orderNumberCin7.trim();
    const skus = packageCompositionSkus(orderItems);
    if (!orderNumber || skus.length === 0) {
      setBoxSuggestionError('Missing order number.');
      return;
    }
    setBoxDimsSaving(true);
    setBoxSuggestionError('');
    try {
      await matchedBoxShipStationWriteRef.current;
      await saveOrderPackageDimensions(shipStationOrderId, {
        orderNumber,
        length: boxSuggestion.length,
        width: boxSuggestion.width,
        height: boxSuggestion.height,
        skus,
      });
      setBoxSuggestion(null);
      setShowSuccess(true);
      setTimeout(() => setShowSuccess(false), 1000);
    } catch (error: unknown) {
      console.error('Error saving suggested box dimensions:', error);
      setBoxSuggestionError(getApiErrorMessage(error, 'Failed to save box dimensions.'));
    } finally {
      setBoxDimsSaving(false);
    }
  };

  const closeBoxScan = (): void => {
    boxScanBusyRef.current = false;
    boxDimRetakeRef.current = false;
    setBoxScanBusy(false);
    setBoxScanError('');
    setPendingBoxDims(null);
    setBoxScanOpen(false);
  };

  const handleBoxSizeCapture = async (imageSrc: string | null): Promise<boolean> => {
    if (!imageSrc || boxScanBusyRef.current || boxDimsSaving || pendingBoxDims) return false;
    boxScanBusyRef.current = true;
    setBoxScanBusy(true);
    setBoxScanError('');
    try {
      const scan = await readBoxDimensionsFromImage(imageSrc);
      if (scan.status === BOX_SCAN_STATUSES.NO_TEXT) {
        setBoxScanError(BOX_SCAN_NO_TEXT_MESSAGE);
        return false;
      }
      if (scan.status === BOX_SCAN_STATUSES.NO_DIMS) {
        setBoxScanError(boxScanNoDimsMessage(scan.ocrText));
        return false;
      }
      setPendingBoxDims(scan.dims);
      return true;
    } catch (error: unknown) {
      console.error('Error reading box size:', error);
      setBoxScanError(BOX_SCAN_NO_TEXT_MESSAGE);
      return false;
    } finally {
      boxScanBusyRef.current = false;
      setBoxScanBusy(false);
    }
  };

  const confirmBoxDimensions = async (): Promise<void> => {
    const retake = boxDimRetakeRef.current;
    if (!pendingBoxDims || !shipStationOrderId || boxDimsSaving) return;
    const orderNumber = orderNumberCin7.trim();
    if (!orderNumber) {
      setBoxScanError('Missing order number.');
      return;
    }
    const skus = packageCompositionSkus(orderItems);
    if (skus.length === 0) {
      setBoxScanError('Missing SKUs for this order.');
      return;
    }
    setBoxDimsSaving(true);
    setBoxScanError('');
    try {
      await matchedBoxShipStationWriteRef.current;
      await saveOrderPackageDimensions(shipStationOrderId, {
        orderNumber,
        length: pendingBoxDims.length,
        width: pendingBoxDims.width,
        height: pendingBoxDims.height,
        skus,
      });
      setPendingBoxDims(null);
      setBoxScanOpen(false);
      boxDimRetakeRef.current = false;
      if (retake && settings.photographPackingItems) {
        openPackingPhoto(true);
        return;
      }
      setShowSuccess(true);
      setTimeout(() => setShowSuccess(false), 1000);
    } catch (error: unknown) {
      console.error('Error saving box dimensions:', error);
      setBoxScanError(getApiErrorMessage(error, 'Failed to save box dimensions.'));
    } finally {
      setBoxDimsSaving(false);
    }
  };

  const clearPackingPhotoSequence = (): void => {
    packingPhotoOpenGenerationRef.current += 1;
    packingPhotoSequenceRef.current = null;
    packingPhotoSequencePromiseRef.current = null;
  };

  const startBoxSizeLookup = (): void => {
    boxLookupResultRef.current = undefined;
    if (!settings.scanSuggestBoxSize) {
      boxLookupRef.current = null;
      boxLookupResultRef.current = null;
      return;
    }
    const skus = packageCompositionSkus(orderItems);
    if (skus.length === 0) {
      boxLookupRef.current = Promise.resolve(null);
      boxLookupResultRef.current = null;
      return;
    }
    const pending = lookupOrderPackageDimensions(skus)
      .then((result) => (
        result.found
          ? { length: result.length, width: result.width, height: result.height }
          : null
      ))
      .catch((error: unknown) => {
        console.error('Error looking up a prior box size:', error);
        return null;
      });
    boxLookupRef.current = pending;
    void pending.then((suggestion) => {
      if (boxLookupRef.current !== pending) return;
      boxLookupResultRef.current = suggestion;
    });
  };

  const openPackingPhoto = (retake = false): void => {
    setPackingPhotoRetake(retake);
    setWebcamOn(false);
    setMessage('');
    releasePackingPhotoStill();
    setPackingPhotoError('');
    setBoxSuggestion(null);
    setBoxSuggestionError('');
    setBoxSelectPrompt(false);
    boxStepGenerationRef.current += 1;
    if (!retake) {
      startBoxSizeLookup();
    }
    const generation = packingPhotoOpenGenerationRef.current + 1;
    packingPhotoOpenGenerationRef.current = generation;
    packingPhotoSequenceRef.current = null;
    const orderNumber = orderNumberCin7.trim();
    if (!orderNumber) {
      packingPhotoSequencePromiseRef.current = null;
      setPackingPhotoError('Missing order number.');
      setPackingPhotoPhase(PACKING_PHOTO_PHASES.LIVE);
      return;
    }
    const pending = fetchNextPackingPhotoSequence(orderNumber);
    packingPhotoSequencePromiseRef.current = pending;
    void pending.then((sequence) => {
      if (packingPhotoOpenGenerationRef.current !== generation) return;
      packingPhotoSequenceRef.current = sequence;
    }).catch((error: unknown) => {
      if (packingPhotoOpenGenerationRef.current !== generation) return;
      setPackingPhotoError(getApiErrorMessage(error, 'Failed to prepare packing photo.'));
    });
    setPackingPhotoPhase(PACKING_PHOTO_PHASES.LIVE);
  };

  const submitWithoutPhotoStep = async (): Promise<void> => {
    boxStepGenerationRef.current += 1;
    startBoxSizeLookup();
    const result = await handleSubmit({ flash: false });
    if (result === 'Order submitted successfully!') {
      await finishPostSubmit(PACKING_SUBMIT_FLASHES.SUCCESS);
    }
  };

  const dismissPackingPhotoSheet = (): void => {
    clearPackingPhotoSequence();
    setPackingPhotoPhase(null);
    releasePackingPhotoStill();
    setPackingPhotoError('');
    setPackingPhotoRetake(false);
  };

  // Android Chrome often discards a backgrounded tab holding the camera; the saved step
  // lets the reload land back on the same post-submit screen instead of the nav bar.
  useEffect(() => {
    if (postSubmitReadyKeyRef.current !== sessionKey) return;
    let step: PostSubmitStep | null = null;
    if (isSubmitted) {
      if (boxScanOpen) {
        step = { kind: POST_SUBMIT_STEP_KINDS.BOX_SCAN, retake: boxDimRetakeRef.current, pendingDims: pendingBoxDims };
      } else if (boxSuggestion) {
        step = { kind: POST_SUBMIT_STEP_KINDS.BOX_SUGGESTION, dimensions: boxSuggestion };
      } else if (boxSelectPrompt) {
        step = { kind: POST_SUBMIT_STEP_KINDS.BOX_SELECT };
      } else if (packingPhotoPhase != null && packingPhotoRetake) {
        step = { kind: POST_SUBMIT_STEP_KINDS.PACKING_PHOTO_RETAKE };
      }
    }
    writePostSubmitStep(sessionKey, step);
  }, [
    sessionKey,
    isSubmitted,
    boxScanOpen,
    pendingBoxDims,
    boxSuggestion,
    boxSelectPrompt,
    packingPhotoPhase,
    packingPhotoRetake,
  ]);

  useEffect(() => {
    if (!postSubmitRestore || postSubmitRestore.orderId !== sessionKey) return;
    const { submitted, step } = postSubmitRestore;
    setPostSubmitRestore(null);
    postSubmitReadyKeyRef.current = sessionKey;
    if (!submitted) {
      writePostSubmitStep(sessionKey, null);
      return;
    }
    if (!step) return;
    switch (step.kind) {
      case POST_SUBMIT_STEP_KINDS.BOX_SELECT:
        finishPhotoBoxStep(null);
        return;
      case POST_SUBMIT_STEP_KINDS.BOX_SUGGESTION:
        finishPhotoBoxStep(step.dimensions);
        return;
      case POST_SUBMIT_STEP_KINDS.BOX_SCAN:
        openBoxScan(step.retake);
        if (step.pendingDims) setPendingBoxDims(step.pendingDims);
        return;
      case POST_SUBMIT_STEP_KINDS.PACKING_PHOTO_RETAKE:
        openPackingPhoto(true);
        return;
    }
  }, [postSubmitRestore, sessionKey, finishPhotoBoxStep, openBoxScan, openPackingPhoto]);

  const closePackingPhoto = (): void => {
    if (packingPhotoBusyRef.current) return;
    if (packingPhotoRetake) {
      dismissPackingPhotoSheet();
      return;
    }
    void declinePackingPhoto();
  };

  const declinePackingPhoto = async (): Promise<void> => {
    if (packingPhotoBusyRef.current) return;
    packingPhotoBusyRef.current = true;
    releasePackingPhotoStill();
    setPackingPhotoError('');
    setPackingPhotoBusyLabel('Submitting order');
    setPackingPhotoSubmitting(true);
    try {
      const result = await handleSubmit({ flash: false });
      if (result !== 'Order submitted successfully!') {
        setPackingPhotoError(result && result.length > 0 ? result : 'Failed to submit order.');
        return;
      }
      await finishPostSubmit(PACKING_SUBMIT_FLASHES.NO_PHOTO);
    } catch (error: unknown) {
      console.error('Error submitting order without packing photo:', error);
      setPackingPhotoError(getApiErrorMessage(error, 'Failed to submit order.'));
    } finally {
      packingPhotoBusyRef.current = false;
      setPackingPhotoSubmitting(false);
      setPackingPhotoBusyLabel('Saving packing photo');
    }
  };

  const handlePackingPhotoShutter = (): void => {
    if (packingPhotoBusyRef.current) return;
    const canvas = packingPhotoViewportRef.current?.captureFullFrameCanvas();
    if (!canvas) return;
    let frame: HTMLCanvasElement;
    try {
      frame = copyPackingFrame(canvas);
    } catch (error: unknown) {
      console.error('Error copying packing photo:', error);
      setPackingPhotoError(getApiErrorMessage(error, 'Failed to prepare packing photo.'));
      return;
    }
    const generation = packingPhotoOpenGenerationRef.current;
    releasePackingPhotoStill();
    setPackingPhotoFrame(frame);
    setPackingPhotoPhase(PACKING_PHOTO_PHASES.REVIEW);
    setPackingPhotoError('');
    const job = startPackingJpegEncode(frame);
    packingJpegJobRef.current = job;
    void job.promise.then((blob) => {
      if (packingJpegJobRef.current !== job) return;
      if (packingPhotoOpenGenerationRef.current !== generation) return;
      packingPhotoBlobRef.current = blob;
      packingJpegJobRef.current = null;
    }).catch((error: unknown) => {
      if (packingJpegJobRef.current !== job) return;
      packingJpegJobRef.current = null;
      if (isPackingJpegCancelled(error)) return;
      if (packingPhotoOpenGenerationRef.current !== generation) return;
      console.error('Error encoding packing photo:', error);
      setPackingPhotoError(getApiErrorMessage(error, 'Failed to prepare packing photo.'));
    });
  };

  const retakePackingPhoto = (): void => {
    releasePackingPhotoStill();
    setPackingPhotoError('');
    setPackingPhotoPhase(PACKING_PHOTO_PHASES.LIVE);
  };

  const confirmPackingPhoto = async (): Promise<void> => {
    if (
      packingPhotoPhase !== PACKING_PHOTO_PHASES.REVIEW
      || packingPhotoFrame == null
      || packingPhotoSubmitting
      || packingPhotoBusyRef.current
    ) {
      return;
    }
    const orderNumber = orderNumberCin7.trim();
    if (!orderNumber) {
      setPackingPhotoError('Missing order number.');
      return;
    }
    packingPhotoBusyRef.current = true;
    setPackingPhotoSubmitting(true);
    setPackingPhotoError('');
    try {
      const pendingSequence = packingPhotoSequencePromiseRef.current;
      const sequence = packingPhotoSequenceRef.current ?? (pendingSequence ? await pendingSequence : null);
      if (sequence == null) {
        setPackingPhotoError('Failed to prepare packing photo.');
        return;
      }
      let jpeg = packingPhotoBlobRef.current;
      if (!jpeg) {
        const job = packingJpegJobRef.current;
        if (!job) {
          setPackingPhotoError('Failed to prepare packing photo.');
          return;
        }
        jpeg = await job.promise;
        packingPhotoBlobRef.current = jpeg;
      }
      await savePackingPhoto({ orderNumber, sequence, jpeg });
      if (packingPhotoRetake) {
        dismissPackingPhotoSheet();
        setShowSuccess(true);
        setTimeout(() => setShowSuccess(false), 1000);
        return;
      }
      const result = await handleSubmit({ flash: false });
      if (result === 'Order submitted successfully!') {
        await finishPostSubmit(PACKING_SUBMIT_FLASHES.SUCCESS);
        return;
      }
      setPackingPhotoPhase(null);
      releasePackingPhotoStill();
    } catch (error: unknown) {
      if (isPackingJpegCancelled(error)) return;
      console.error('Error saving packing photo:', error);
      setPackingPhotoError(getApiErrorMessage(error, 'Failed to save packing photo.'));
    } finally {
      packingPhotoBusyRef.current = false;
      setPackingPhotoSubmitting(false);
    }
  };
  const handleReset = async (): Promise<void> => {
    if (resetting) return;
    setResetting(true);
    try {
      const orderNumber = orderNumberCin7.trim();
      if (orderNumber) {
        await deletePackingLogImages(orderNumber);
        await deletePackingLog(orderNumber);
      }
      if (sessionKey) {
        await clearOrderPickScans(sessionKey);
      }
      handleResetCore(orderItems, setOrderItems, setMessage, setIsReadyForSubmit);
      setPendingBinScans({});
      setIsSubmitted(false);
      setPackingPhotoRetake(false);
      boxDimRetakeRef.current = false;
      clearPackingPhotoSequence();
      setPackingPhotoPhase(null);
      releasePackingPhotoStill();
      setPackingPhotoError('');
      boxStepGenerationRef.current += 1;
      boxLookupRef.current = null;
      boxLookupResultRef.current = undefined;
      setBoxSuggestion(null);
      setBoxSuggestionError('');
      setBoxSelectPrompt(false);
      closeBoxScan();
    } catch (error: unknown) {
      console.error('Error resetting order:', error);
      setMessage(getApiErrorMessage(error, 'Failed to reset order.'));
    } finally {
      setResetting(false);
    }
  };

  const handleRelease = async (reason: OrderReleaseReason): Promise<void> => {
    if (!shipStationOrderId || userTagId == null || releasing) {
      return;
    }

    setReleasing(true);
    try {
      await releaseOrder(shipStationOrderId, userTagId, reason);
      setShowReleasePicker(false);
      setSelectedReleaseReason(null);
      setOrderItems([]);
      setShipToName('');
      setOrderNumberCin7('');
      setTitle('');
      setSelectedOrderId('');
      navigate('/order-manager');
    } catch (error: unknown) {
      console.error('Error releasing order:', error);
      setMessage(getApiErrorMessage(error, 'Failed to release order'));
    } finally {
      setReleasing(false);
    }
  };

  const handleConfirmSplit = async (): Promise<void> => {
    if (splitBoxes == null || splitSubmitting || !shipStationOrderId) return;
    setSplitSubmitting(true);
    setMessage('');
    try {
      await splitOrderInShipStation(
        shipStationOrderId,
        splitBoxesToRequest(buildSplitLines(orderItems), splitBoxes),
      );
      setSplitBoxes(null);
      setIsSubmitted(true);
      setShowSuccess(true);
      setTimeout(() => setShowSuccess(false), 1000);
    } catch (error: unknown) {
      console.error('Error splitting shipment in ShipStation:', error);
      setMessage(getApiErrorMessage(error, 'Failed to split shipment in ShipStation.'));
    } finally {
      setSplitSubmitting(false);
    }
  };

  const handleReprintOrderLabel = async (): Promise<void> => {
    if (reprintingOrderLabel) return;
    const orderNumber = orderNumberCin7.trim();
    const customerName = shipToName.trim();
    if (!orderNumber || !customerName) {
      setMessage('Order is missing an order number or customer name.');
      return;
    }
    setReprintingOrderLabel(true);
    try {
      await printQrLabel(orderNumber, customerName);
      setMessage(ORDER_LABEL_SENT_MESSAGE);
    } catch (error: unknown) {
      console.error('Error printing order label:', error);
      setMessage(getApiErrorMessage(error, 'Failed to print label'));
    } finally {
      setReprintingOrderLabel(false);
    }
  };

  const viewportMessageOverlay =
    webcamOn && message ? (
      <div className={orderDetailMessageClass(message)} role="status">
        {message}
      </div>
    ) : null;

  const boxScanOverlay = boxScanBusy ? (
    <div className="order-detail-message order-detail-message-default" role="status">
      Reading box size...
    </div>
  ) : boxScanError ? (
    <div className="order-detail-message order-detail-message-error" role="alert">
      {boxScanError}
    </div>
  ) : null;

  const packingPhotoOpen = packingPhotoPhase != null;
  const captureOverlayOpen = packingPhotoOpen || boxScanOpen || boxSuggestion != null || boxSelectPrompt;
  const barVariant: PickScanBarVariant = captureOverlayOpen
    ? PICK_SCAN_BAR_VARIANTS.CAMERA
    : splitShipmentOpen
      ? PICK_SCAN_BAR_VARIANTS.SUBMIT
      : isSubmitted
        ? PICK_SCAN_BAR_VARIANTS.NAV
        : isReadyForSubmit
          ? PICK_SCAN_BAR_VARIANTS.SUBMIT
          : PICK_SCAN_BAR_VARIANTS.CAMERA;
  const showPuzzle = !captureOverlayOpen
    && barVariant !== PICK_SCAN_BAR_VARIANTS.NAV
    && orderBenefitsFromPuzzleView(orderItems);
  const orderActionsMenu = (
    <OrderActionsMenu
      open={actionMenuOpen}
      onToggle={() => setActionMenuOpen((prev) => !prev)}
      resetting={resetting}
      onReset={() => {
        setActionMenuOpen(false);
        void handleReset();
      }}
      canRelease={canReleaseOrder}
      releasing={releasing}
      onRelease={() => {
        setActionMenuOpen(false);
        setShowReleasePicker(true);
      }}
      onSplitShipment={isAdmin ? () => {
        setActionMenuOpen(false);
        setWebcamOn(false);
        setSplitBoxes(initialSplitBoxes(buildSplitLines(orderItems)));
        setSplitHoldMode(SPLIT_HOLD_MODES.HALF);
      } : undefined}
      onReprintOrderLabel={settings.printScanOrderLabel ? () => {
        setActionMenuOpen(false);
        void handleReprintOrderLabel();
      } : undefined}
      reprintingOrderLabel={reprintingOrderLabel}
      onMarkScanned={isAdmin && !isSubmitted ? handleMarkScanned : undefined}
      showSubmittedActions={isSubmitted}
      onRetakePackingImage={settings.photographPackingItems ? () => {
        setActionMenuOpen(false);
        openPackingPhoto(true);
      } : undefined}
      onRetakeBoxDimImage={settings.scanSuggestBoxSize ? () => {
        setActionMenuOpen(false);
        openBoxScan(true);
      } : undefined}
    />
  );
  const cameraLeftActions = (
    <div className="order-detail-bottom-bar-left-group">
      <button
        type="button"
        className="order-detail-scan-mode-toggle"
        onClick={toggleScanMode}
        aria-pressed={qrActive}
        title={qrActive ? 'Switch to text (OCR) scan' : 'Switch to QR code scan'}
        aria-label={qrActive ? 'Scan mode: QR. Switch to text.' : 'Scan mode: text. Switch to QR.'}
      >
        <span className={`order-detail-scan-mode-option${qrActive ? ' active' : ''}`}>
          <QrPlaceholderIcon />
        </span>
        <span className={`order-detail-scan-mode-option${!qrActive ? ' active' : ''}`}>
          <span className="placeholder-text-chip">a..z</span>
        </span>
      </button>
    </div>
  );
  const navLeftActions = (
    <>
      <button
        type="button"
        onClick={() => navigate('/orders-list')}
        className="order-detail-bottom-bar-nav-btn"
      >
        All Orders
      </button>
      <button
        type="button"
        onClick={() => navigate('/my-orders')}
        className="order-detail-bottom-bar-nav-btn"
      >
        My Orders
      </button>
      <button
        type="button"
        onClick={() => navigate('/pick-next-order')}
        className="order-detail-bottom-bar-nav-btn"
      >
        Pick Next
      </button>
    </>
  );
  const splitConfirmReady = splitBoxes != null && canConfirmSplit(splitBoxes);
  const leftActions = captureOverlayOpen
    ? undefined
    : barVariant === PICK_SCAN_BAR_VARIANTS.NAV
      ? navLeftActions
      : barVariant === PICK_SCAN_BAR_VARIANTS.CAMERA
        ? cameraLeftActions
        : undefined;
  const centerActions = captureOverlayOpen
    ? undefined
    : splitShipmentOpen ? (
      <button
        type="button"
        disabled={!splitConfirmReady || splitSubmitting}
        onClick={() => { void handleConfirmSplit(); }}
        className={`order-detail-submit-btn split-shipment-confirm-btn ${splitConfirmReady && !splitSubmitting ? 'active' : ''}`}
      >
        {splitSubmitting ? 'Splitting…' : 'Confirm'}
      </button>
    ) : barVariant === PICK_SCAN_BAR_VARIANTS.CAMERA ? (
      <button
        type="button"
        className="order-detail-bar-camera-btn order-detail-bar-camera-btn-center"
        onClick={() => setWebcamOn(true)}
        aria-label="Open camera"
      >
        <CameraIcon size={28} />
      </button>
    ) : barVariant === PICK_SCAN_BAR_VARIANTS.SUBMIT ? (
      <button
        type="button"
        onClick={() => {
          if (settings.photographPackingItems) {
            openPackingPhoto();
            return;
          }
          void submitWithoutPhotoStep();
        }}
        disabled={!isReadyForSubmit}
        className={`order-detail-submit-btn ${isReadyForSubmit ? 'active' : ''}`}
      >
        Submit Order
      </button>
    ) : undefined;
  const splitQuantityHold = splitHoldMode === SPLIT_HOLD_MODES.QUANTITY;
  const splitHoldToggle = (
    <button
      type="button"
      className="order-detail-scan-mode-toggle split-shipment-hold-toggle"
      onClick={() => setSplitHoldMode(splitQuantityHold ? SPLIT_HOLD_MODES.HALF : SPLIT_HOLD_MODES.QUANTITY)}
      aria-pressed={splitQuantityHold}
      title={splitQuantityHold ? 'Hold splits in half' : 'Hold asks for a quantity'}
      aria-label={splitQuantityHold
        ? 'Hold mode: quantity. Switch to split half.'
        : 'Hold mode: split half. Switch to quantity.'}
    >
      <span className={`order-detail-scan-mode-option${!splitQuantityHold ? ' active' : ''}`}>
        <FaFingerprint className="split-shipment-hold-icon" aria-hidden="true" />
      </span>
      <span className={`order-detail-scan-mode-option${splitQuantityHold ? ' active' : ''}`}>
        <FaHashtag className="split-shipment-hold-icon" aria-hidden="true" />
      </span>
    </button>
  );
  const rightActions = captureOverlayOpen ? undefined : splitShipmentOpen ? splitHoldToggle : (
    <>
      {showPuzzle && (
        <button
          type="button"
          className={`scanner-control-btn components-only-btn ${componentsOnlyMode ? 'components-only-on' : ''}`}
          onClick={() => setComponentsOnlyMode((prev) => !prev)}
          title={componentsOnlyMode ? 'Show full list' : 'Consolidate by SKU'}
        >
          <FaPuzzlePiece className="scanner-control-icon" size={24} />
        </button>
      )}
      {orderActionsMenu}
    </>
  );

  if (loading) {
    return (
      <div className="order-detail-container">
        <OrderDetailNavbar
          orderNumber={orderNumberCin7}
          orderName={shipToName}
          isInternational={isInternational}
        />
        <h1>Loading...</h1>
        <OrderDetailCameraBar open={false} />
      </div>
    );
  }

  return (
    <div
      className={`order-detail-container${webcamOn ? ' order-detail-camera-open' : ''}`}
    >
      {showSuccess && (
        <div className={orderFlash === PACKING_SUBMIT_FLASHES.NO_PHOTO ? 'success-flash no-packing-photo-flash' : 'success-flash'}>
          <div className="success-message">
            {orderFlash === PACKING_SUBMIT_FLASHES.NO_PHOTO ? '⚠️ No Packing Photo Taken' : '✓ Success!'}
          </div>
        </div>
      )}
      <OrderDetailNavbar
        orderNumber={orderNumberCin7}
        orderName={shipToName}
        isInternational={isInternational}
        onClose={splitShipmentOpen ? () => setSplitBoxes(null) : undefined}
      />

      {SHOW_ORDER_DETAIL_SCANNER && (
      <div className="pbl-section order-detail-scanner-section">
        <div className="scanner-controls-row">
          <button
            type="button"
            className={`scanner-control-btn camera-toggle-btn ${webcamOn ? 'camera-on' : ''}`}
            onClick={() => setWebcamOn((prev) => !prev)}
            title={webcamOn ? 'Turn off camera' : 'Turn on camera'}
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className="scanner-control-icon">
              <path d="M2 8H4L6 5H18L20 8H22V19H2V8Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              <circle cx="12" cy="13" r="4" stroke="currentColor" strokeWidth="2"/>
            </svg>
          </button>
        </div>
        <ScannerSection
          mode={qrActive ? 'qr' : 'barcode'}
          onCapture={qrActive ? handleQrCapture : handleCapture}
          webcamOn={webcamOn}
          setWebcamOn={setWebcamOn}
          className="webcam-capture-container"
          overlay={viewportMessageOverlay}
        />
      </div>
      )}

      {message && !webcamOn ? (
        <div className="order-detail-flash" aria-live="polite">
          <div className={orderDetailMessageClass(message)}>
            {message}
          </div>
        </div>
      ) : null}

      {splitBoxes != null ? (
        <SplitShipmentView
          lines={buildSplitLines(orderItems)}
          boxes={splitBoxes}
          holdMode={splitHoldMode}
          onChange={setSplitBoxes}
        />
      ) : (
        <OrderItemsList
          orderItems={orderItems}
          orderScope={sessionKey}
          pickListComponentsOnly={pickListComponentsOnly}
          skusWithNotes={skusWithNotes}
          observeItem={observeItem}
          onSelectItem={setSelectedProductInfo}
          onSelectShelf={handleSelectShelf}
          shelfScanEnabled={webcamOn}
        />
      )}

      {selectedProductInfo && (
        <ProductInfoModal
          item={selectedProductInfo}
          onClose={() => setSelectedProductInfo(null)}
        />
      )}

      {showCheckSlackModal && (
        <div
          className="order-detail-check-slack-overlay"
          role="dialog"
          aria-modal="true"
          aria-labelledby="check-slack-message"
        >
          <div className="order-detail-check-slack-dialog">
            <p id="check-slack-message" className="order-detail-check-slack-message">
              Check Slack for details on fulfilling order #{orderNumberCin7}
            </p>
            <button
              type="button"
              className="order-detail-check-slack-btn"
              onClick={() => setCheckSlackAcknowledged(true)}
            >
              Checked
            </button>
          </div>
        </div>
      )}

      {showReleasePicker && (
        <div className="order-detail-release-overlay" role="dialog" aria-modal="true" aria-labelledby="release-order-title">
          <div className="order-detail-release-dialog">
            <h3 id="release-order-title" className="order-detail-release-title">Release order</h3>
            <p className="order-detail-release-copy">Why are you releasing this order?</p>
            <div className="order-detail-release-reasons">
              {(Object.entries(RELEASE_REASON_LABELS) as [OrderReleaseReason, string][]).map(([reason, label]) => (
                <button
                  key={reason}
                  type="button"
                  className={`order-detail-release-reason-btn${selectedReleaseReason === reason ? ' selected' : ''}`}
                  disabled={releasing}
                  aria-pressed={selectedReleaseReason === reason}
                  onClick={() => setSelectedReleaseReason(reason)}
                >
                  {label}
                </button>
              ))}
            </div>
            <button
              type="button"
              className={`order-detail-release-confirm-btn${selectedReleaseReason != null ? ' active' : ''}`}
              disabled={selectedReleaseReason == null || releasing}
              onClick={() => {
                if (selectedReleaseReason != null) {
                  void handleRelease(selectedReleaseReason);
                }
              }}
            >
              {releasing ? 'Releasing…' : 'Release Order'}
            </button>
            <button
              type="button"
              className="order-detail-release-cancel-btn"
              disabled={releasing}
              onClick={() => setShowReleasePicker(false)}
            >
              Cancel
            </button>
          </div>
        </div>
      )}
      {packingPhotoPhase != null ? (
        <PackingPhotoSheet
          phase={packingPhotoPhase}
          stillSrc={null}
          stillFrame={packingPhotoFrame}
          submitting={packingPhotoSubmitting}
          busyLabel={packingPhotoBusyLabel}
          error={packingPhotoError}
          viewportRef={packingPhotoViewportRef}
          onClose={closePackingPhoto}
          onRetake={retakePackingPhoto}
          onConfirm={() => { void confirmPackingPhoto(); }}
        />
      ) : null}
      {boxSelectPrompt ? (
        <SelectBoxPrompt onScan={() => openBoxScan()} onSkip={() => setBoxSelectPrompt(false)} />
      ) : null}
      {boxSuggestion ? (
        <SuggestedBoxSheet
          dimensions={boxSuggestion}
          saving={boxDimsSaving}
          error={boxSuggestionError}
          onConfirm={() => { void acceptSuggestedBox(); }}
          onDecline={declineSuggestedBox}
        />
      ) : null}
      {boxScanOpen && pendingBoxDims ? (
        <BoxDimensionConfirmModal
          message={boxDimensionConfirmMessage(pendingBoxDims, orderNumberCin7)}
          saving={boxDimsSaving}
          onConfirm={() => { void confirmBoxDimensions(); }}
          onCancel={() => setPendingBoxDims(null)}
        />
      ) : null}
      <OrderDetailCameraBar
        open={packingPhotoPhase === PACKING_PHOTO_PHASES.LIVE || boxScanOpen || webcamOn}
        suppressSheet={packingPhotoPhase === PACKING_PHOTO_PHASES.LIVE}
        sheetLabel={boxScanOpen ? 'Scan box size' : undefined}
        shutterDisabled={boxScanOpen && (boxScanBusy || boxDimsSaving || pendingBoxDims != null)}
        onShutter={
          packingPhotoPhase === PACKING_PHOTO_PHASES.LIVE
            ? handlePackingPhotoShutter
            : () => scannerCaptureRef.current?.capture()
        }
        barVariant={barVariant}
        leftActions={leftActions}
        centerActions={centerActions}
        rightActions={rightActions}
      >
        {boxScanOpen ? (
          <ScannerSection
            key="box-scan"
            ref={scannerCaptureRef}
            mode="barcode"
            onCapture={handleBoxSizeCapture}
            webcamOn
            setWebcamOn={(on) => {
              if (!on) closeBoxScan();
            }}
            className="webcam-capture-container"
            overlay={boxScanOverlay}
            cornerAction={
              <button
                type="button"
                className="webcam-viewport-close-btn"
                onClick={(event) => {
                  event.stopPropagation();
                  closeBoxScan();
                }}
                aria-label="Close box size scan"
              >
                ×
              </button>
            }
          />
        ) : packingPhotoPhase == null ? (
          <ScannerSection
            ref={scannerCaptureRef}
            mode={qrActive ? 'qr' : 'barcode'}
            onCapture={qrActive ? handleQrCapture : handleCapture}
            webcamOn={webcamOn}
            setWebcamOn={setWebcamOn}
            className="webcam-capture-container"
            overlay={viewportMessageOverlay}
            cornerAction={
              <button
                type="button"
                className="webcam-viewport-close-btn"
                onClick={(event) => {
                  event.stopPropagation();
                  setWebcamOn(false);
                }}
                aria-label="Close camera"
              >
                ×
              </button>
            }
          />
        ) : null}
      </OrderDetailCameraBar>
    </div>
  );
}

export default OrderDetail;
